// frontend/src/components/Sidebar.tsx
import React from 'react';
import { Files, Plus, Trash2, FileCode, Folder as FolderIcon, FolderOpen, FilePlus, FolderPlus } from 'lucide-react';
import { FakeFile, Folder } from '../types';

interface Props {
  folders: Folder[];
  files: FakeFile[];
  activeFileId: string | null;
  onSelectFile: (id: string) => void;
  onCreateFile: (folderId: string | null) => void;
  onCreateFolder: (parentId: string | null) => void; // Updated signature
  onDeleteFile: (id: string) => void;
  onDeleteFolder: (id: string) => void;
  onToggleFolder: (id: string) => void;
}

export const Sidebar: React.FC<Props> = ({ 
  folders, files, activeFileId, 
  onSelectFile, onCreateFile, onCreateFolder, 
  onDeleteFile, onDeleteFolder, onToggleFolder 
}) => {
  
  // --- RECURSIVE TREE RENDERER ---
  const renderTree = (parentId: string | null, depth: number) => {
    // 1. Find items at this level
    const currentFolders = folders.filter(f => f.parentId === parentId);
    const currentFiles = files.filter(f => f.folderId === parentId);

    return (
      <>
        {/* Render Folders */}
        {currentFolders.map(folder => (
          <div key={folder.id}>
            <div 
              className="folder-item group" // 'group' class for hover effects
              style={{ paddingLeft: `${depth * 15 + 20}px` }}
              onClick={() => onToggleFolder(folder.id)}
            >
               <div style={{ display:'flex', alignItems:'center', gap:'6px', overflow:'hidden', flex: 1 }}>
                   {folder.isOpen ? <FolderOpen size={14} color="#cca700"/> : <FolderIcon size={14} color="#cca700"/>}
                   <span className="truncate">{folder.name}</span>
               </div>

               {/* HOVER ACTIONS */}
               <div className="folder-actions">
                  <button 
                    className="action-btn" 
                    title="New File"
                    onClick={(e) => { e.stopPropagation(); onCreateFile(folder.id); }}
                  >
                    <FilePlus size={13} />
                  </button>
                  <button 
                    className="action-btn" 
                    title="New Subfolder"
                    onClick={(e) => { e.stopPropagation(); onCreateFolder(folder.id); }}
                  >
                    <FolderPlus size={13} />
                  </button>
                  <button 
                    className="action-btn delete-btn" 
                    title="Delete Folder"
                    onClick={(e) => { e.stopPropagation(); onDeleteFolder(folder.id); }}
                  >
                    <Trash2 size={13} />
                  </button>
               </div>
            </div>
            
            {/* Recursion: Render children if open */}
            {folder.isOpen && renderTree(folder.id, depth + 1)}
          </div>
        ))}

        {/* Render Files */}
        {currentFiles.map(file => (
          <div 
            key={file.id}
            className={`file-item group ${activeFileId === file.id ? 'active' : ''}`}
            style={{ paddingLeft: `${depth * 15 + 20}px` }} // Indent files slightly more? No, align with folders usually.
            onClick={(e) => { e.stopPropagation(); onSelectFile(file.id); }}
          >
            <div style={{ display:'flex', alignItems:'center', gap:'6px', overflow:'hidden', flex: 1 }}>
              <FileCode size={14} style={{ minWidth: '14px' }} color={file.language === 'python' ? '#ffd700' : '#e34c26'} />
              <span className="truncate">{file.name}</span>
            </div>
            
            <button 
              className="action-btn delete-btn" 
              onClick={(e) => { e.stopPropagation(); onDeleteFile(file.id); }}
            >
              <Trash2 size={13} />
            </button>
          </div>
        ))}
      </>
    );
  };

  return (
    <div className="sidebar-wrapper">
      <div className="activity-bar">
        <Files size={24} color="white" />
      </div>

      <div className="sidebar-panel">
        <div className="sidebar-header">
            <span>EXPLORER</span>
            <div style={{ display:'flex', gap:'5px' }}>
                <button onClick={() => onCreateFile(null)} className="btn" title="New Root File"><FilePlus size={14}/></button>
                <button onClick={() => onCreateFolder(null)} className="btn" title="New Root Folder"><FolderPlus size={14}/></button>
            </div>
        </div>
        
        <div className="grow" style={{ overflowY: 'auto' }}>
            <div className="workspace-title">WORKSPACE</div>
            {/* Start recursion at Root (null) */}
            {renderTree(null, 0)}
        </div>
      </div>
    </div>
  );
};